import { useContext } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { lighten } from '@mui/material/styles';

import { DraggableDialog } from '../../DraggablePaper/DraggablePaper';
import ModalFormTitle from '../../Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle';
import { Context as ThemeContext } from '../../../api/theme';
import { Context as DesignTokensContext } from '../../../api/designTokens';
import { DEFAULT_TOKENS } from '../../../styles/designTokens';
import { DEFAULT_SURFACE, THEME_PRESETS, ThemePreset, ThemePresetId } from '../../../styles/themePresets';

type ColorThemeDialogProps = { open: boolean; onClose: () => void };

/** A miniature of the app — header, sidebar, a card and a button — in one preset's colours. */
function PresetSwatch({ preset, brand }: { preset: ThemePreset; brand: typeof DEFAULT_TOKENS.color }) {
    const color = { ...brand, ...preset.color };
    const surface = preset.surface ?? DEFAULT_SURFACE;
    return (
        <Box
            sx={{
                height: 52,
                borderRadius: '6px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${surface.border}`,
                bgcolor: surface.page
            }}
        >
            <Box sx={{ height: 10, bgcolor: color.header }} />
            <Box sx={{ flex: 1, display: 'flex' }}>
                <Box sx={{ width: 16, bgcolor: lighten(color.primary, 0.95), borderRight: `1px solid ${surface.border}` }} />
                <Box sx={{ flex: 1, p: '5px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Box sx={{ height: 13, borderRadius: '3px', bgcolor: surface.card, border: `1px solid ${surface.border}` }} />
                    <Box sx={{ width: 20, height: 6, borderRadius: '2px', bgcolor: color.secondary }} />
                </Box>
            </Box>
        </Box>
    );
}

export default function ColorThemeDialog({ open, onClose }: ColorThemeDialogProps) {
    const { mode, toggleThemeMode, themePreset, setThemePreset } = useContext(ThemeContext) as any;
    const { tokens } = useContext(DesignTokensContext);
    const brand = { ...DEFAULT_TOKENS.color, ...tokens?.color };
    const isDark = mode === 'dark';

    return (
        <DraggableDialog
            open={open}
            onClose={onClose}
            hideBackdrop
            disableEnforceFocus
            maxWidth={false}
            PaperProps={{ sx: { width: 380, borderRadius: '10px' } }}
        >
            <ModalFormTitle title="Colour Theme" toggleDialog={onClose} titleBarStyle={{ padding: '6px 12px 6px 16px' }} />
            <DialogContent sx={{ px: 1.5, pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" component="p" sx={{ color: 'text.secondary', fontSize: '0.72rem', mb: 1 }}>
                    {isDark
                        ? 'Colour themes apply in light mode. Switch dark mode off to choose one.'
                        : 'Changes the header, menu and background. Saved to your account.'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.5 }}>
                    {THEME_PRESETS.map((preset) => {
                        const selected = preset.id === themePreset;
                        return (
                            <ButtonBase
                                key={preset.id}
                                disabled={isDark}
                                onClick={() => setThemePreset(preset.id as ThemePresetId)}
                                aria-pressed={selected}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    gap: 0.5,
                                    p: 0.75,
                                    borderRadius: '8px',
                                    outline: selected ? '2px solid' : 'none',
                                    outlineColor: 'text.primary',
                                    bgcolor: selected ? 'action.selected' : 'transparent',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '&.Mui-disabled': { opacity: 0.45 }
                                }}
                            >
                                <PresetSwatch preset={preset} brand={brand} />
                                <Typography sx={{ fontSize: '0.72rem', textAlign: 'center', color: 'text.primary' }}>
                                    {preset.label}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
                <FormControlLabel
                    sx={{ mt: 1, ml: 0 }}
                    control={<Switch size="small" checked={isDark} onChange={toggleThemeMode} />}
                    label={<Typography sx={{ fontSize: '0.8rem' }}>Dark mode</Typography>}
                />
            </DialogContent>
        </DraggableDialog>
    );
}
